import { TextWritingType } from './text-layer';

/** 拉伸选项类型 */
export enum StretchOptionType {
    /** 禁用 不可变形 */
    none = 0,
    /** 拉伸完全填充满 */
    fill = 1,
    /** 无拉伸完全填充满 */
    cover = 2,
    /** 重复铺满 */
    repeat = 3,
}

/** 拉伸选项值 */
export const FitTypeValue = {
    [StretchOptionType.none]: '',
    [StretchOptionType.cover]: 'cover',
    [StretchOptionType.fill]: '100% 100%',
};

/** 图片变换参数 */
export interface IImageTransform {
    offsetX: number;
    offsetY: number;
    rotate: number;
    scale: number;
    /** 图层内容水平翻转 */
    flipH: boolean;
    /** 图层内容垂直翻转 */
    flipV: boolean;
    widthRatio: number;
    heightRatio: number;
}

/** 图片属性 */
export interface IImageProps {
    /** 图片地址 */
    url: string;
    /** 图片自然宽度 */
    naturalWidth: number;
    /** 图片自然高度 */
    naturalHeight: number;
    /** 图片变换参数 */
    imageTransform: IImageTransform;
    /** 拉伸选项 */
    stretchOption: StretchOptionType;
}

/** 蒙版类型 */
export enum MaskType {
    /** 无蒙版 */
    none = 0,
    cut = 1,
    imgLayer = 2,
}

export interface IShadow {
    [index: string]: string | number;
    x: number;
    y: number;
    blur: number;
    color: string;
    opacity: number;
}
export interface IShadowParams {
    x?: number;
    y?: number;
    blur?: number;
    color?: string;
    opacity?: number;
}

/** 发光 */
export interface IImageEffectGlow {
    id?: string;
    color: string;
    width: number;
    hidden?: boolean;
    // 是否可编辑
    editable?: boolean;
}

export enum IImageEffectShadowType {
    base = 1,
    lightProjection = 2,
    reflection = 3,
    waterSurface = 4,
}

/** 阴影 */
export interface IImageEffectShadow {
    /** 阴影类型 */
    type?: IImageEffectShadowType;
    /** 阴影旋转角度 */
    rotate?: number;
    /** 光照投影方向 */
    direction?: number;
    /** 投影距离(不同投影类型距离概念不一样) */
    distance?: number;
    /** 倒影亮度 */
    brightness?: number;
    /** 倒影透明度 */
    opacity?: number;
    /** 水面波纹大小 */
    wave?: number;
    /** 水面波纹数量 */
    waveNum?: number;
    /** 阴影id */
    id?: string;
    /** 阴影颜色 */
    color?: string;
    /** 阴影x坐标 */
    x?: number;
    /** 阴影y坐标 */
    y?: number;
    /** 阴影半径 */
    blur?: number;
    /** 阴影是否隐藏 */
    hidden?: boolean;
    /** 阴影是否可编辑 */
    editable?: boolean;
}

// 描边类型
export enum ImageEffectStrokeType {
    // 常规
    normal = 0,

    // 外扩实现
    outSolidLine = 1,

    // 外扩虚线
    outDashedLine = 2,

    // 撕纸
    tornPaper = 4,
}
/** 描边 */
export interface IImageEffectStroke {
    id?: string;
    color: string;
    width: number;
    hidden?: boolean;
    // 是否可编辑
    editable?: boolean;
    strokeType: ImageEffectStrokeType;
}

/** 图片特效 */
export interface IImageEffectProps {
    effectAlpha: number;
    glow: IImageEffectGlow[];
    shadow: IImageEffectShadow[];
    stroke: IImageEffectStroke[];
    screenshotUrl: string;
    advancedSetting?: boolean;
    effectPosition?: {
        left: number;
        top: number;
        right: number;
        bottom: number;
    };
}

/** 滤镜  仅移动端用 */
export interface IImageFilterProps {
    effectAlpha: number;
}

/** AI花字拓展 */
export interface IImageAiTextExtension {
    /** 文本文字 */
    text: string;
    /** 文本字体族 */
    fontFamily: string;
    /** 粗体 */
    bold: boolean;
    /** 斜体 */
    italic: boolean;
    /** 文本方向 */
    writingMode: TextWritingType;
    /** 效果id */
    formulaId: number;
}

/**
 * 混合模式
 */
export enum BlendModeType {
    /**
     * 混合模式：正常（无混合模式）
     */
    MTBlendNormal = 0,

    /**
     * 混合模式：正片叠底
     */
    MTBlendModeMultiply = 1,

    /**
     * 混合模式：叠加
     */
    MTBlendModeOverlay = 2,

    /**
     * 混合模式：线性减淡
     */
    MTBlendModeLinearDodage = 3,

    /**
     * 混合模式：线性加深
     */
    MTBlendModeLinearBurn = 4,

    /**
     * 混合模式：滤色
     */
    MTBlendModeScreen = 5,

    /**
     * 混合模式：柔光
     */
    MTBlendModeSoftLight = 6,

    /**
     * 混合模式：亮光
     */
    MTBlendModeVividLight = 7,

    /**
     * 混合模式：线性光
     */
    MTBlendModeLinearLight = 8,

    /**
     * 混合模式：差值
     */
    MTBlendModeDifference = 9,

    /**
     * 混合模式：颜色加深
     */
    MTBlendModeColorBurn = 10,

    /**
     * 混合模式：颜色减淡
     */
    MTBlendModeColorDodge = 11,

    /**
     * 混合模式：排除
     */
    MTBlendModeExclusion = 12,

    /**
     * 混合模式：变暗
     */
    MTBlendModeDarken = 13,

    /**
     * 混合模式：变暗
     */
    MTBlendModeLighten = 14,

    /**
     * 混合模式：点光
     */
    MTBlendModePinLight = 15,

    /**
     * 混合模式：强光
     */
    MTBlendModeHardLight = 16,
}

export interface IBorderRadiusProps {
    leftTop: number;
    rightTop: number;
    leftBottom: number;
    rightBottom: number;
    hidden: boolean;
}

export interface IMcpEffectProps {
    params: {
        [key: string]: any;
    };
    materialID: number;
}

export interface IFillGradientProps {
    angle: number;
    colors: {
        color: string;
        value: number;
    }[];
}

export interface IImageFillProps {
    color: string;
    gradientColor: IFillGradientProps;
}

export interface IImageLayerProps extends IImageProps {
    maskURL: string;
    maskType: MaskType;
    cutoutMaskURL: string;
    shadow?: IShadow;

    // 图片特效
    effect?: IImageEffectProps | null;

    // 图片滤镜
    filter?: IImageFilterProps | null;

    // 客户端显示提示按钮
    hint?: boolean;

    // （图片编辑器使用）通过创建画布来初始化，主图设为透明
    isTransparent?: boolean;

    // AI花字拓展
    aiTextExtension?: IImageAiTextExtension | null;

    // 2024-05-06修改蒙板缩放规则 v15.2.0
    // 蒙版羽化值0~100
    maskFeathering?: number;

    // 是否应用最新的蒙版规则
    minimumMask?: boolean;

    blendMode?: BlendModeType;

    // 图层圆角
    borderRadius?: IBorderRadiusProps | null;

    // MCP特效
    mcpEffects?: IMcpEffectProps[];

    // 填充颜色
    fill?: IImageFillProps;
}
